/**
 * 🔴🔴 前端唯一的两种失败类型 —— 分开是因为**它们的文案来源不同**。
 *
 * - {@link ApiError}：请求到了后端，后端给了判定。
 *   ⇒ 文案**必须**用 `message`（后端原句），前端一个字都不许改写。
 * - {@link TransportError}：请求**根本没到后端**（网络断、代理没起、响应不是 JSON）。
 *   ⇒ 后端没说话，这一种也只能由前端出文案 —— 这是唯一的例外。
 *
 * ⛔ 绝不允许把两者合并成一句「操作失败，请重试」：
 * 后端有些码的语义正好是**不许重试**（12023 缺权限，重试永远不会成功；
 * 12013 凭据不可读，重试永远不会成功）。见 erp-web/README §四。
 */

/** 后端明确给出了判定的失败。`message` 是后端原句，直接渲染。 */
export class ApiError extends Error {
  /** 后端业务错误码（`R.code`），非 0 */
  readonly code: number
  /** HTTP 状态码。业务失败大多是 200；越权 403；未登录 401；未预期异常 500 */
  readonly httpStatus: number
  /** 后端链路 id，排查时给客服/运维用；可能为 null */
  readonly traceId: string | null

  constructor(code: number, message: string, httpStatus: number, traceId: string | null) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.httpStatus = httpStatus
    this.traceId = traceId
  }
}

/**
 * 请求没有到达后端，或后端回了一份读不懂的东西。
 *
 * ⚠️ 这一类**不带业务码** —— 前端不许给它编一个码，
 * 编了之后按码分支的代码会把「网络断了」当成某个业务判定处理。
 */
export class TransportError extends Error {
  /** 有 HTTP 响应但不是合法信封时带上状态码；连响应都没有时为 null */
  readonly httpStatus: number | null

  constructor(message: string, httpStatus: number | null) {
    super(message)
    this.name = 'TransportError'
    this.httpStatus = httpStatus
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

export function isTransportError(e: unknown): e is TransportError {
  return e instanceof TransportError
}

/**
 * 把任意异常翻成**可以直接显示给用户的一句话**。
 *
 * 🔴 对 {@link ApiError} 返回的就是后端原句，没有任何加工。
 */
export function displayMessageOf(e: unknown): string {
  if (isApiError(e)) {
    return e.message
  }
  if (isTransportError(e)) {
    return e.message
  }
  if (e instanceof Error && e.message) {
    return e.message
  }
  return '发生了一个没有任何描述的错误 —— 这本身是个缺陷，请把这句话连同操作步骤反馈给我们'
}
